import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiMessageSquare,
  FiSettings,
  FiShield,
  FiAlertTriangle,
  FiFileText,
  FiDatabase,
  FiPieChart,
  FiFolder,
  FiExternalLink,
  FiBookOpen,
} from 'react-icons/fi';

// Types
interface QNavSidebarProps {
  isOpen: boolean;
}

const QNavSidebar: React.FC<QNavSidebarProps> = ({ isOpen }) => {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`bg-background-menu text-white h-full overflow-y-auto transition-all duration-300 ${
        isOpen ? 'w-64' : 'w-0'
      }`}
    >
      {isOpen && (
        <div className="py-4">
          {/* Navigation sections */}
          <div className="px-4 mb-6">
            <h2 className="text-xs uppercase tracking-wider text-secondary-400 font-semibold mb-2">Navigation</h2>
            <nav>
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/dashboard"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard') &&
                      !isActive('/dashboard/chat') &&
                      !isActive('/dashboard/analytics') &&
                      !isActive('/dashboard/risk')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiHome className="h-5 w-5 mr-3" />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/chat/csm"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard/chat')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiMessageSquare className="h-5 w-5 mr-3" />
                    <span>Navigator Chat</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/dashboard/customer-analytics"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard/customer-analytics') || isActive('/dashboard/customer-metrics')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiPieChart className="h-5 w-5 mr-3" />
                    <span>Customer Analytics</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/customers"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard/customers')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiFolder className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Engagement Document Management</span>
                  </Link>
                </li>
                <li style={{ display: 'none' }}>
                  <Link
                    to="/dashboard/engagement"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard/engagement')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiAlertTriangle className="h-5 w-5 mr-3" />
                    <span>Engagement Management</span>
                  </Link>
                </li>
                <li style={{ display: 'none' }}>
                  <Link
                    to="/dashboard/governance"
                    className={`flex items-center px-3 py-2 rounded-qnav ${
                      isActive('/dashboard/governance')
                        ? 'bg-primary-700 text-white'
                        : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white'
                    }`}
                  >
                    <FiFileText className="h-5 w-5 mr-3" />
                    <span>Governance</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Additional Resources */}
          <div className="px-4 mb-6">
            <h2 className="text-xs uppercase tracking-wider text-secondary-400 font-semibold mb-2">
              Additional Resources
            </h2>
            <nav>
              <ul className="space-y-1">
                <li>
                  <a
                    href="https://ask.pathfinder.wwso.aws.dev/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-3 py-2 rounded-qnav text-secondary-300 hover:bg-secondary-700/50 hover:text-white"
                  >
                    <FiExternalLink className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>AWS Pathfinder</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-center px-3 py-2 rounded-qnav text-gray-500 cursor-not-allowed">
                    <FiExternalLink className="h-5 w-5 mr-3 flex-shrink-0 opacity-50" />
                    <span>Amazon Quick Suite Enterprise Adoption Guide (Coming soon)</span>
                  </div>
                </li>
                <li>
                  <a
                    href="https://aws.highspot.com/items/67d43f12c239dd92e349e937?lfrm=srp.4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-3 py-2 rounded-qnav text-secondary-300 hover:bg-secondary-700/50 hover:text-white"
                  >
                    <FiExternalLink className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Kiro/Amazon Q Developer Enterprise Adoption Guide</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          {/* Settings & Support */}
          <div className="px-4">
            <h2 className="text-xs uppercase tracking-wider text-secondary-400 font-semibold mb-2">
              Settings & Support
            </h2>
            <nav>
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/dashboard/settings"
                    className="flex items-center px-3 py-2 rounded-qnav text-secondary-300 hover:bg-secondary-700/50 hover:text-white"
                  >
                    <FiSettings className="h-5 w-5 mr-3" />
                    <span>Settings (Under Construction)</span>
                  </Link>
                </li>
                <li style={{ display: 'none' }}>
                  <Link
                    to="/dashboard/security"
                    className="flex items-center px-3 py-2 rounded-qnav text-secondary-300 hover:bg-secondary-700/50 hover:text-white"
                  >
                    <FiShield className="h-5 w-5 mr-3" />
                    <span>Security</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Branding */}
          <div className="px-4 mt-8 pt-6 border-t border-secondary-700">
            <div className="flex items-center">
              <img src="/logo.png" alt="Amazon Q Logo" className="h-6 w-6" />
              <div className="ml-2">
                <div className="text-xs text-secondary-400">Built by</div>
                <div className="text-sm font-medium text-brand-orange">Amazon Q Developer CLI</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export { QNavSidebar };
